import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clapperboard } from 'lucide-react';
import Rail from '../../ui/Rail';
import PersonCard from '../../ui/PersonCard';
import { getCredits } from '../../services/tmdbService';
import { actorPath } from '../../lib/routes';

/**
 * Distribution d'un titre — chaque visage ouvre la fiche de l'acteur.
 *
 * Remplace `CastSection` / `CastSectionWithFallback` : mêmes données, mais
 * cliquables, mutualisées par `tmdbService` (plus de requête refaite à chaque
 * ouverture de fiche) et repliées sur les noms du catalogue si TMDB est
 * injoignable — le rail reste alors utilisable, sans photo.
 *
 * @param {number|string} tmdbId
 * @param {'film'|'serie'} type
 * @param {string[]} [fallbackCast] noms issus du catalogue local
 * @param {string}   [director]
 */
export default function CastRail({ tmdbId, type = 'film', fallbackCast = [], director }) {
  const navigate = useNavigate();
  const [cast, setCast] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSettled(false);

    if (!tmdbId) {
      setSettled(true);
      return () => {
        cancelled = true;
      };
    }

    getCredits(tmdbId, type).then((credits) => {
      if (cancelled) return;
      setCast(credits.cast.filter((person) => person.photo).slice(0, 20));
      setDirectors(credits.directors.slice(0, 3));
      setSettled(true);
    });

    return () => {
      cancelled = true;
    };
  }, [tmdbId, type]);

  const open = (name) => navigate(actorPath(name));

  // Repli : TMDB muet ou sans photo → on affiche quand même les noms du
  // catalogue, en médaillon d'initiales. Le lien vers la fiche fonctionne.
  const people = cast.length
    ? cast.map((person) => ({
        name: person.name,
        photo: person.photo,
        role: person.character,
      }))
    : fallbackCast.slice(0, 12).map((name) => ({ name, photo: null, role: null }));

  const crew = directors.length
    ? directors
    : director
      ? [{ tmdbId: null, name: director, photo: null }]
      : [];

  if (!settled && !fallbackCast.length) return null;
  if (!people.length && !crew.length) return null;

  return (
    <div className="stack gap-6">
      {crew.length > 0 && (
        <div>
          <p className="eyebrow mb-2 flex items-center gap-2">
            <Clapperboard className="w-3.5 h-3.5" aria-hidden="true" />
            {crew.length > 1 ? 'Réalisation' : 'Réalisé par'}
          </p>
          <div className="flex flex-wrap gap-2">
            {crew.map((person) => (
              <button
                key={person.name}
                type="button"
                className="chip"
                onClick={() => open(person.name)}
              >
                {person.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {people.length > 0 && (
        <Rail
          title="Têtes d'affiche"
          icon={<Users aria-hidden="true" />}
          subtitle="Touchez un visage pour voir sa filmographie"
          itemClass="rail-item-face"
        >
          {people.map((person, index) => (
            <PersonCard
              key={`${person.name}-${index}`}
              index={index}
              name={person.name}
              photo={person.photo}
              role={person.role}
              onOpen={open}
            />
          ))}
        </Rail>
      )}
    </div>
  );
}
